<template>
  <v-card class="mt-4">
    <v-card-title class="text-h6"> Kommentek </v-card-title>

    <v-divider />

    <v-card-text>
      <div class="activity-container">
        <template v-if="loading">
          <v-skeleton-loader
            v-for="i in 3"
            :key="i"
            type="avatar, list-item-three-line"
            class="mb-3"
          />
        </template>

        <div v-else-if="activities.length === 0" class="empty-state">
          <v-icon size="48" color="grey-lighten-1">
            mdi-comment-outline
          </v-icon>

          <div class="empty-title">Még nincs aktivitás</div>

          <div class="empty-subtitle">
            A kommentek és rendszeresemények itt fognak megjelenni.
          </div>
        </div>

        <div v-else class="activity-feed">
          <div
            v-for="activity in activities"
            :key="activity.id"
            class="activity-item"
          >
            <div class="activity-icon">
              <v-icon :color="getColor(activity)">
                {{ getIcon(activity) }}
              </v-icon>
            </div>

            <div class="activity-content">
              <div class="activity-header">
                <span class="activity-author">
                  {{ getAuthor(activity) }}
                </span>

                <span class="activity-time">
                  {{ formatDate(activity.createdAt) }}
                </span>
              </div>

              <div
                class="activity-message"
                :class="{ system: activity.actorType === 'SYSTEM' }"
              >
                {{ activity.message }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>
<script setup>
import { ref, onMounted } from "vue";
import { ActivityService } from "@/services/ActivityService";
import { useAuthStore } from "@/store/AuthStore";
const props = defineProps({
  publicReference: String,
});

let basePath = "/activities";
const authStore = useAuthStore();
if (authStore.isAdmin) {
  basePath = "/admin/activities";
}
const activityService = new ActivityService(basePath);
const activities = ref([]);
const loading = ref(true);

onMounted(async () => {
  await fetchTimeline();
});

async function fetchTimeline() {
  try {
    loading.value = true;
    const res = await activityService.listComments(props.publicReference);
    activities.value = res.data;
  } catch (e) {
    console.error("Failed to load activities", e);
  } finally {
    loading.value = false;
  }
}

function getColor(activity) {
  if (activity.actorType === "ADMIN") return "blue";
  if (activity.actorType === "CUSTOMER") return "green";
  return "grey";
}

function getIcon(activity) {
  switch (activity.type) {
    case "COMMENT":
      return "mdi-comment-outline";
    case "OFFER_CREATED":
      return "mdi-file-plus-outline";
    case "BOOKING_CREATED":
      return "mdi-calendar-check";
    default:
      return "mdi-information-outline";
  }
}

function getAuthor(activity) {
  if (activity.actorType === "SYSTEM") return "System";
  return activity.userEmail ?? activity.actorType;
}

function formatDate(date) {
  return new Date(date).toLocaleString();
}

defineExpose({
  fetchTimeline,
});
</script>

<style scoped>
.activity-container {
  max-width: 700px;
  margin: auto;
}

.toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.comment-box {
  margin-bottom: 20px;
  padding: 12px;
}

.comment-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.activity-feed {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.activity-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  background: #fafafa;
  border: 1px solid #eee;
}

.activity-icon {
  width: 32px;
  display: flex;
  justify-content: center;
}

.activity-content {
  flex: 1;
}

.activity-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.activity-author {
  font-weight: 600;
}

.activity-time {
  font-size: 12px;
  color: #888;
}

.activity-message {
  font-size: 14px;
}

.activity-message.system {
  color: #666;
  font-style: italic;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #888;
}

.empty-title {
  margin-top: 10px;
  font-weight: 600;
  font-size: 16px;
}

.empty-subtitle {
  font-size: 13px;
  margin-top: 4px;
}
</style>
